import { Box, Fab, Paper, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { usePermissions } from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { NAVY, GOLD, GOLD_HOVER } from '../styles/ds';
import { useAssistant } from './AssistantContext';
import AssistantConversation from './AssistantConversation';
import assistantIcon from '../assets/assistant-icon.png';

// Bubble (fixed bottom-left) + compact chat panel. Mounted once in
// CustomLayout.js, above the router, so it — and the conversation it
// reads through useAssistant() — survives route navigation. Renders
// nothing on the full /assistant route (that page renders the same
// conversation itself) and nothing for non-admins, same gate CustomMenu.js
// already uses for /audit and /users.
const AssistantWidget = () => {
  const { permissions } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const { view, setView } = useAssistant();
  const isMobile = useMediaQuery('(max-width:900px)');

  const isAdmin = permissions === 'admin';
  const onFullPage = location.pathname === '/assistant';

  if (!isAdmin || onFullPage) return null;

  const isOpen = view === 'compact';

  const handleExpand = () => navigate('/assistant');
  const handleClose = () => setView('closed');
  const handleToggle = () => setView(isOpen ? 'closed' : 'compact');

  return (
    <>
      {isOpen && (
        <Paper
          className="crm-assistant-panel"
          elevation={8}
          sx={{
            position: 'fixed',
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: isMobile ? 0 : 3,
            // `right` here, not `left`: this app runs under stylis-plugin-rtl
            // (see crm/src/index.js), which flips physical left/right in every
            // emitted rule — so `right` in this sx is what actually renders on
            // the visual LEFT edge of the screen, per the spec's "fixed
            // bottom-left" requirement.
            ...(isMobile
              ? { top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }
              // bottom: 24 (Fab offset) + 76 (Fab height) + 16 (gap) = 116, so
              // the panel clears the now-bigger bubble instead of overlapping it.
              : { bottom: 116, right: 24, width: 380, height: 520, maxHeight: 'calc(100vh - 140px)' }),
          }}
        >
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 1.25, backgroundColor: NAVY, color: '#fff', flexShrink: 0,
          }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>עוזר חכם</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="הרחב למסך מלא">
                <IconButton size="small" onClick={handleExpand} sx={{ color: '#fff' }}>
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="סגור">
                <IconButton size="small" onClick={handleClose} sx={{ color: '#fff' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, backgroundColor: '#faf8f5' }}>
            <AssistantConversation variant="compact" />
          </Box>
        </Paper>
      )}

      {/* On mobile the panel is fullscreen — don't leave the bubble floating
          clickable on top of it; only show it when the panel isn't open. */}
      {!(isMobile && isOpen) && (
        <Fab
          className="crm-assistant-launcher"
          onClick={handleToggle}
          aria-label="עוזר חכם"
          sx={{
            // `right`, not `left` — see the comment on the panel's Paper above;
            // stylis-plugin-rtl flips it to render on the visual left.
            position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
            width: 76, height: 76,
            backgroundColor: GOLD,
            '&:hover': { backgroundColor: GOLD_HOVER },
          }}
        >
          <img src={assistantIcon} alt="" style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
        </Fab>
      )}
    </>
  );
};

export default AssistantWidget;
